import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  DEFAULT_SEARCH_STATE,
  parseSearchParams,
  serializeSearchState,
  type SearchState,
} from './search-query';

type ArrayFilterKey = 'type' | 'config' | 'listingStatus' | 'areas' | 'amenities';

export interface UseSearchState {
  state: SearchState;
  /** Merge a partial update. Resets to page 1 unless `page` is what changed. */
  setFilters: (patch: Partial<SearchState>) => void;
  /** Toggle a value in one of the multi-select array filters. */
  toggleArrayValue: (key: ArrayFilterKey, value: string) => void;
  setSort: (sort: SearchState['sort']) => void;
  setPage: (page: number) => void;
  clear: () => void;
}

/**
 * Reads/writes the search state to the URL query string (source of truth).
 * Every mutation navigates via `setSearchParams`, so the back button, sharing
 * and refresh all behave correctly.
 */
export function useSearchState(): UseSearchState {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => parseSearchParams(searchParams), [searchParams]);

  // Filter/sort edits replace the current history entry — otherwise every
  // checkbox tick or keystroke (min/max price) pushed its own entry, and the
  // back button stepped through individual filter changes instead of leaving
  // the search page. Page changes still push, since stepping back through
  // result pages is a real, expected back-button use case.
  const commit = useCallback(
    (next: SearchState, options: { push?: boolean } = {}) => {
      setSearchParams(serializeSearchState(next), { replace: !options.push });
    },
    [setSearchParams],
  );

  const setFilters = useCallback(
    (patch: Partial<SearchState>) => {
      const changingPageOnly = Object.keys(patch).length === 1 && 'page' in patch;
      commit({ ...state, ...patch, page: changingPageOnly ? (patch.page ?? 1) : 1 });
    },
    [commit, state],
  );

  const toggleArrayValue = useCallback(
    (key: ArrayFilterKey, value: string) => {
      const current = state[key] as string[];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      commit({ ...state, [key]: next, page: 1 } as SearchState);
    },
    [commit, state],
  );

  const setSort = useCallback(
    (sort: SearchState['sort']) => commit({ ...state, sort, page: 1 }),
    [commit, state],
  );

  const setPage = useCallback(
    (page: number) => commit({ ...state, page }, { push: true }),
    [commit, state],
  );

  const clear = useCallback(() => commit({ ...DEFAULT_SEARCH_STATE }), [commit]);

  return { state, setFilters, toggleArrayValue, setSort, setPage, clear };
}
